/* Copyright 2026 WinSolutions. SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial */
// Capture is requested during a user gesture, then consumed by the ordinary recorder.
const pending = new Map<
  string,
  { stream: Promise<MediaStream>; cancel: () => void }
>();
export async function requestMeetingCapture(): Promise<MediaStream> {
  try {
    return navigator.mediaDevices.getDisplayMedia({
      video: { displaySurface: "browser" },
      audio: false,
      // @ts-expect-error Chromium capture hints are not yet in TypeScript DOM definitions.
      preferCurrentTab: true,
      surfaceSwitching: "exclude",
      monitorTypeSurfaces: "exclude",
      systemAudio: "exclude",
      selfBrowserSurface: "include",
    });
  } catch (error) {
    return Promise.reject(error);
  }
}
export function cancelMeetingCapture(roomId: string): void {
  pending.get(roomId)?.cancel();
  pending.delete(roomId);
  timers.delete(roomId);
}
export function prepareMeetingCapture(roomId: string): void {
  cancelMeetingCapture(roomId);
  let cancelled = false;
  const stream = requestMeetingCapture().then((value) => {
    if (cancelled) value.getTracks().forEach((track) => track.stop());
    return value;
  });
  void stream.catch(() => undefined); // The recorder will surface cancellation/permission errors.
  const timeout = window.setTimeout(() => cancelMeetingCapture(roomId), 60000);
  pending.set(roomId, {
    stream,
    cancel: () => {
      cancelled = true;
      window.clearTimeout(timeout);
      void stream.then(
        (value) => value.getTracks().forEach((track) => track.stop()),
        () => undefined,
      );
    },
  });
  // Consuming transfers ownership of the stream and cancels this expiry.
  timers.set(roomId, timeout);
}
const timers = new Map<string, number>();
export async function consumeMeetingCapture(
  roomId: string,
): Promise<MediaStream> {
  const value = pending.get(roomId);
  if (!value) return requestMeetingCapture();
  pending.delete(roomId);
  window.clearTimeout(timers.get(roomId));
  timers.delete(roomId);
  return value.stream.then((stream) => {
    if (!stream.getVideoTracks().some((track) => track.readyState === "live"))
      throw new Error("Захват окна завершён. Запустите запись снова.");
    return stream;
  });
}
